/**
 * 应用入口
 * @module main
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { App, ConfigProvider } from 'antd';
import { router } from '@/app/routes';
import { AntdAppBridge } from '@/app/antdApp';
import { appFullName } from '@/app/appMeta';
import { UiConfigContext, defaultUiConfig } from '@/app/uiConfig';
import { UiLanguageProvider, useUiLanguage } from '@/i18n';
import { backendGetUiConfig } from '@/services/backend';
import './styles/global.css';
import './styles/theme.light.css';
import './styles/theme.dark.css';

document.title = appFullName;

const getWindowSectionTitle = (pathname: string, t: (text: string) => string): string => {
  if (pathname.startsWith('/editor/')) {
    return t('编程区域');
  }

  if (pathname === '/marketplace' || pathname.startsWith('/marketplace/') || pathname === '/market' || pathname.startsWith('/market/')) {
    return t('应用市场');
  }

  if (pathname === '/admin' || pathname.startsWith('/admin/')) {
    return t('管理系统');
  }

  return t('我的应用');
};

const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem('yiblock.theme');
  if (stored === 'light' || stored === 'dark') return stored;
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
};

document.documentElement.setAttribute('data-theme', getInitialTheme());

const RootApp: React.FC = () => {
  const { antdLocale, t } = useUiLanguage();
  const [uiConfig, setUiConfig] = React.useState(defaultUiConfig);

  React.useEffect(() => {
    void backendGetUiConfig().then(setUiConfig).catch(() => undefined);
  }, []);

  React.useEffect(() => {
    const handleUiConfigUpdate = (event: Event) => {
      setUiConfig((event as CustomEvent<typeof defaultUiConfig>).detail);
    };
    window.addEventListener('ui-config-updated', handleUiConfigUpdate);
    return () => window.removeEventListener('ui-config-updated', handleUiConfigUpdate);
  }, []);

  React.useEffect(() => {
    const updateWindowTitle = (pathname: string) => {
      document.title = `${appFullName} - ${getWindowSectionTitle(pathname, t)}`;
    };

    updateWindowTitle(router.state.location.pathname);

    const unsubscribe = router.subscribe((state) => {
      updateWindowTitle(state.location.pathname);
    });

    return unsubscribe;
  }, [t]);

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 6,
        },
      }}
    >
      <App>
        <UiConfigContext.Provider value={uiConfig}>
          <AntdAppBridge />
          <RouterProvider
            router={router}
            future={{
              v7_startTransition: true,
            }}
          />
          {uiConfig.showFooter && (
            <footer className="app-footer" dangerouslySetInnerHTML={{ __html: uiConfig.footerHtml }} />
          )}
        </UiConfigContext.Provider>
      </App>
    </ConfigProvider>
  );
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <UiLanguageProvider>
      <RootApp />
    </UiLanguageProvider>
  </React.StrictMode>
);
